import { useCallback, useContext, useRef, useSyncExternalStore } from 'react'

import { PlatformContext } from './PlatformProvider/context'
import type { Insets, Platform, PlatformHaptic, PlatformViewport } from './types'

export function usePlatform(): Platform {
  const platform = useContext(PlatformContext)
  if (!platform) {
    throw new Error('usePlatform должен вызываться внутри <PlatformProvider>')
  }
  return platform
}

export function useHaptic(): PlatformHaptic {
  return usePlatform().haptic
}

/** События, после которых стартовый параметр имеет смысл перечитать. */
const START_PARAM_EVENTS = ['visibilitychange', 'focus', 'pageshow'] as const

function subscribeToStartParam(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}

  for (const event of START_PARAM_EVENTS) window.addEventListener(event, onStoreChange)
  return () => {
    for (const event of START_PARAM_EVENTS) window.removeEventListener(event, onStoreChange)
  }
}

/**
 * Стартовый параметр диплинка (`inv_<token>` / `ref_<userId>`), перечитываемый.
 *
 * Снимка при монтировании не хватает. Telegram обещает `start_param` «при запуске», но запуск
 * по диплинку — не всегда новая загрузка вебвью: если мини-апп уже открыт, клиент может просто
 * вывести его вперёд, обновив `initDataUnsafe` на месте. Раз события об этом нет, перечитываем
 * по возвращению приложения на передний план — этим `visibilitychange` и `focus` и являются.
 *
 * Значение сравнивается по строке, поэтому лишних перерисовок нет: пока параметр не менялся,
 * `useSyncExternalStore` отдаёт то же самое.
 */
export function useStartParam(): string | null {
  const platform = usePlatform()
  const getSnapshot = useCallback(() => platform.startParam, [platform])

  return useSyncExternalStore(subscribeToStartParam, getSnapshot, getSnapshot)
}

function insetsEqual(a: Insets, b: Insets): boolean {
  return a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left
}

function viewportEqual(a: PlatformViewport, b: PlatformViewport): boolean {
  return (
    a.height === b.height &&
    a.isExpanded === b.isExpanded &&
    insetsEqual(a.safeArea, b.safeArea) &&
    insetsEqual(a.contentSafeArea, b.contentSafeArea)
  )
}

/**
 * Реактивный viewport: размер окна и safe area меняются при развороте, повороте и в шапке
 * Telegram — подписка живёт в адаптере (`onViewportChanged`), а не тут: лезть в
 * `window.Telegram` из хука — то же нарушение правила 6, только этажом выше.
 *
 * Для раскладки этот хук не нужен: высоты и отступы приезжают CSS-переменными из
 * `ViewportRuntime`, и перерисовки React ради них не требуется.
 */
export function useViewport(): PlatformViewport {
  const platform = usePlatform()
  const cacheRef = useRef<PlatformViewport>(platform.viewport)

  const getSnapshot = useCallback(() => {
    const next = platform.viewport
    if (viewportEqual(next, cacheRef.current)) {
      return cacheRef.current
    }
    cacheRef.current = next
    return next
  }, [platform])

  const subscribe = useCallback(
    (onStoreChange: () => void) => platform.onViewportChanged(onStoreChange),
    [platform],
  )

  return useSyncExternalStore(subscribe, getSnapshot)
}
